import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Video, Loader2, Check, X, Copy, Scissors, Trash2, Share2, AlertTriangle, EyeOff } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { UpgradeCard } from "@/components/UpgradeCard";

const STATUS_STYLE = {
  pending: "border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400",
  approved: "border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  rejected: "border-border bg-muted text-muted-foreground",
};

export function VideoTab({ site }) {
  const siteId = site.id;
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [edit, setEdit] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get(`/sites/${siteId}/videos`);
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load videos");
    }
  };
  useEffect(() => { load(); }, [siteId]);

  const act = async (id, action) => {
    setBusy(id + action);
    try {
      const { data } = await api.post(`/sites/${siteId}/videos/${id}/${action}`);
      setData(data);
      toast.success({ approve: "Live on the site.", reject: "Rejected.", unpublish: "Taken down.",
        delete: "Deleted.", post: "Sent to social." }[action]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "That didn't work");
    } finally {
      setBusy("");
    }
  };

  const saveEdit = async (v) => {
    const e = edit[v.id] || {};
    setBusy(v.id + "edit");
    try {
      const { data } = await api.patch(`/sites/${siteId}/videos/${v.id}`, {
        display_name: e.display_name ?? v.display_name,
        town: e.town ?? v.town,
        ...(e.start || e.end ? { trim: { start: Number(e.start || 0), end: e.end ? Number(e.end) : null } } : {}),
        ...(e.poster_at ? { poster_at: Number(e.poster_at) } : {}),
      });
      setData(data);
      setEdit({ ...edit, [v.id]: {} });
      toast.success("Saved.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save");
    } finally {
      setBusy("");
    }
  };

  if (!data) return <div className="flex justify-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (!data.entitled) {
    return <div className="mx-auto max-w-lg" data-testid="video-upgrade"><UpgradeCard siteId={siteId} offer={data.upgrade} /></div>;
  }

  const wallTag = data.embed_base
    ? `<div id="lp-wall"></div>\n<script async src="${data.embed_base}/api/embed/wall.js" data-lp-wall="${(data.collect_url || "").split("/").pop()}"></script>`
    : "Embed host not set on the server. Set REPUTATION_PUBLIC_BASE (or APP_ORIGIN), then reload.";

  return (
    <div className="space-y-6" data-testid="video-tab">
      <div className="rounded-lg border border-border bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-xl">
            <div className="flex items-center gap-2">
              <Video className="h-4 w-4 text-primary" />
              <p className="font-display text-lg font-semibold tracking-tight">Video testimonials</p>
              <Badge variant="outline" data-testid="video-counts">
                {data.counts.pending} waiting · {data.counts.approved} live
              </Badge>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Customers record 60 seconds on their phone. Nothing appears on the website until you approve it.
              Videos are stored on your own server — {data.counts.approved + data.counts.pending}/{data.quota} used,
              disk {data.disk.used_pct}% full.
            </p>
          </div>
          {data.disk.over_threshold && (
            <p className="flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400"
              data-testid="video-disk-warning">
              <AlertTriangle className="h-3.5 w-3.5" /> Disk is {data.disk.used_pct}% full
            </p>
          )}
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Send this link</p>
            <code className="mt-1 block break-all rounded-md bg-muted px-3 py-2 text-xs" data-testid="video-collect-url">{data.collect_url}</code>
            <Button size="sm" variant="outline" className="mt-2" data-testid="video-copy-link"
              onClick={() => { navigator.clipboard.writeText(data.collect_url); toast.success("Link copied."); }}>
              <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy link
            </Button>
            <p className="mt-2 text-xs text-muted-foreground">
              Send it after the Google review ask — never instead of it.
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Wall of love embed</p>
            <code className="mt-1 block whitespace-pre-wrap break-all rounded-md bg-muted px-3 py-2 text-xs" data-testid="video-wall-tag">{wallTag}</code>
            <Button size="sm" variant="outline" className="mt-2" data-testid="video-copy-wall" disabled={!data.embed_base}
              onClick={() => { navigator.clipboard.writeText(wallTag); toast.success("Embed copied."); }}>
              <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy embed
            </Button>
          </div>
        </div>
      </div>

      {!data.videos.length ? (
        <p className="rounded-lg border border-border bg-card p-5 text-sm text-muted-foreground" data-testid="video-empty">
          No videos yet. Send the link above to a happy customer who already left a Google review.
        </p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2" data-testid="video-list">
          {data.videos.map((v) => {
            const e = edit[v.id] || {};
            return (
              <div key={v.id} className="rounded-lg border border-border bg-card p-4" data-testid={`video-card-${v.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className={`rounded-full border px-2 py-0.5 text-xs ${STATUS_STYLE[v.status]}`}
                    data-testid={`video-status-${v.id}`}>{v.status}</span>
                  <span className="text-xs text-muted-foreground">{Math.round(v.duration)}s · {(v.bytes / 1e6).toFixed(1)} MB</span>
                </div>
                {v.flag && (
                  <p className="mt-2 flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-xs text-amber-600 dark:text-amber-400"
                    data-testid={`video-flag-${v.id}`}>
                    <AlertTriangle className="h-3.5 w-3.5" /> Flagged for a look: {v.flag.reason}
                  </p>
                )}
                <video className="mt-3 w-full rounded-md bg-black" controls preload="none"
                  poster={v.poster} src={v.preview} data-testid={`video-player-${v.id}`} />
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Input defaultValue={v.display_name} placeholder="Shown name" data-testid={`video-name-${v.id}`}
                    onChange={(ev) => setEdit({ ...edit, [v.id]: { ...e, display_name: ev.target.value } })} />
                  <Input defaultValue={v.town || ""} placeholder="Town" data-testid={`video-town-${v.id}`}
                    onChange={(ev) => setEdit({ ...edit, [v.id]: { ...e, town: ev.target.value } })} />
                  <Input type="number" step="0.1" placeholder="Trim from (s)" data-testid={`video-trim-start-${v.id}`}
                    onChange={(ev) => setEdit({ ...edit, [v.id]: { ...e, start: ev.target.value } })} />
                  <Input type="number" step="0.1" placeholder="Trim to (s)" data-testid={`video-trim-end-${v.id}`}
                    onChange={(ev) => setEdit({ ...edit, [v.id]: { ...e, end: ev.target.value } })} />
                </div>
                {v.text && <p className="mt-2 text-sm text-muted-foreground">“{v.text}”</p>}
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <Button size="sm" variant="outline" disabled={busy === v.id + "edit"} data-testid={`video-save-${v.id}`}
                    onClick={() => saveEdit(v)}>
                    <Scissors className="mr-1.5 h-3.5 w-3.5" /> Save edits
                  </Button>
                  {v.status !== "approved" ? (
                    <Button size="sm" disabled={busy === v.id + "approve"} data-testid={`video-approve-${v.id}`}
                      onClick={() => act(v.id, "approve")}>
                      <Check className="mr-1.5 h-3.5 w-3.5" /> Approve
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" disabled={busy === v.id + "unpublish"} data-testid={`video-unpublish-${v.id}`}
                      onClick={() => act(v.id, "unpublish")}>
                      <EyeOff className="mr-1.5 h-3.5 w-3.5" /> Take down
                    </Button>
                  )}
                  {v.status === "approved" && data.auto_post && v.consent?.covers_social && (
                    <Button size="sm" variant="ghost" disabled={busy === v.id + "post"} data-testid={`video-post-${v.id}`}
                      onClick={() => act(v.id, "post")}>
                      <Share2 className="mr-1.5 h-3.5 w-3.5" /> Post to social
                    </Button>
                  )}
                  {v.status !== "rejected" && (
                    <Button size="sm" variant="ghost" disabled={busy === v.id + "reject"} data-testid={`video-reject-${v.id}`}
                      onClick={() => act(v.id, "reject")}>
                      <X className="mr-1.5 h-3.5 w-3.5" /> Reject
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" disabled={busy === v.id + "delete"} data-testid={`video-delete-${v.id}`}
                    onClick={() => act(v.id, "delete")}>
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete
                  </Button>
                </div>
                <p className="mt-2 text-xs text-muted-foreground" data-testid={`video-consent-${v.id}`}>
                  Permission given {new Date(v.consent?.at).toLocaleString()} from {v.consent?.ip}
                  {v.consent?.covers_social ? " · website and social" : " · website only"}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
